import { useFormikContext } from "formik";
import React, { FC, useEffect, useState } from "react";
import { BIO_TNA_EXTENDED_MODE, BIO_TNA_MANDATORY_MODE, BIO_TNA_MODE, BIO_TNA_TEXT_MODE, BIO_TNA_TIMEOUT, BIO_TNA_USER_CONTROL } from "../../constants/biometric-fields-names";
import { CustomFieldBuilder } from "../../containers/utilities/CustomFieldBuilder";
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { DynamicFieldDescriptor } from "../../models/DynamicFieldDescriptor";
import { getPresetInputsForBiometricSettings } from "../../services/biometricPresetService";
import { getErrorMessage } from "../../services/utilities/ServiceError";
import ActivityFrame from "../activity-frame/activity-frame";
import { useDialog } from "../dialog/DialogContext";
import Tabs from "../tabs/tabs";
import ButtonConfiguration from "./ButtonConfiguration";

interface TimeAndAttendanceProps {
    presetId: string;
    isControlId? : boolean
}

export const TimeAndAttendance: FC<TimeAndAttendanceProps> = ({ presetId, isControlId=false }) => {
    const nameAccessor = 'name';
    const formik = useFormikContext();
    const [hasError, setHasError] = useState(false);
    const { translate } = useLocale();
    const customFieldBuilder: CustomFieldBuilder = new CustomFieldBuilder(formik, translate);
    const timeAndAttendanceFields = [BIO_TNA_EXTENDED_MODE, BIO_TNA_MANDATORY_MODE, BIO_TNA_MODE, BIO_TNA_TEXT_MODE, BIO_TNA_TIMEOUT, BIO_TNA_USER_CONTROL]
    const controlIdTimeAndAttendanceFields = [BIO_TNA_MODE, BIO_TNA_TIMEOUT];
    const [supportedFields, setSupportedFields] = useState<DynamicFieldDescriptor[]>([]);
    const [busy, setBusy] = useState(false);
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.general), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.buttonConfiguration), badge: 0, showInitial: true, isActive: true },
    ];
    const [selectedTab, setSelectedTab] = useState(0);
    const dialog = useDialog();

    useEffect(() => {
        formik.validateForm();
    }, [(formik.values as any).custom])
    
    useEffect(() => {        
        const supportedFieldAccessors = supportedFields?.map(field => field.systemName) ?? new Array(0);

        if (formik.errors[nameAccessor]
            || (supportedFieldAccessors.length > 0 && Object.keys(formik.errors as any).length > 0)) {
            setHasError(true)
        }
        else {
            setHasError(false)
        }
    }, [formik.errors]);

    useEffect(() => {
        reloadPage();               
    }, [])

    const reloadPage = async () => {
        try {
            setBusy(true);
            
            const settingData: any = await ( isControlId ? getPresetInputsForBiometricSettings(presetId, controlIdTimeAndAttendanceFields, translate) : getPresetInputsForBiometricSettings(presetId, timeAndAttendanceFields, translate))
            
            if (!!presetId) {
                const currentSupportedFields = settingData.elementFields;
                setSupportedFields(currentSupportedFields);                
            }
            else {
                setSupportedFields(new Array<any>(0));
            }
        }
        catch (error) {
            dialog.pushError({
                title: translate.byKey(TranslationKeys.error),
                message: getErrorMessage(error),
            });
        }
        finally {
            setBusy(false);
        }
    }
    
    const createFields = () => {
        var arrayFields = [] as any[];

        if(isControlId === true){            
            supportedFields?.filter(field => {
                if (controlIdTimeAndAttendanceFields.includes(field.systemName)) {
                    arrayFields.push(field);
                }
            });            
        }
        else {
            supportedFields?.filter(field => {
                if (timeAndAttendanceFields.includes(field.systemName)) {
                    arrayFields.push(field);
                }
            });            
        }

        arrayFields.sort((a, b) => a.settingTypeOrdinal - b.settingTypeOrdinal);

        return arrayFields?.reduce((formFields, field) =>
            customFieldBuilder.buildFormField(formFields, field, false, false, undefined, true),
            new Array(0))
            .map(fieldBundle => <div className="flex flex-row pt-2">{fieldBundle.length === 1
                ? fieldBundle[0].span
                    ? fieldBundle[0].content
                    : <>{fieldBundle[0].content}
                        <div className="flex flex-col flex-1 pl-3"></div></>
                : fieldBundle.map(field => field.content)}</div>)
    }

    const onTabSelect = (selected: number) => {
        setSelectedTab(selected);
    }

    const getContent = (selectedTab) => {
        if (selectedTab === 0){
            return (
                <div className="w-1/2">
                    <div className="mt-3 text-portal-blue-primary-shade2 text-xl font-semibold">
                        {translate.byKey(TranslationKeys.general)}
                    </div>
                    {createFields()}
                </div>
            )
        }
        else if (selectedTab === 1) {
            return <ButtonConfiguration presetId={presetId}/>
        }
    }

    return <ActivityFrame busy={busy} className={`${busy ? 'mt-5 w-full h-full' : ''} flex flex-col flex-grow bg-white`}>
            <Tabs
                tabs={initialTabs}
                onSelect={onTabSelect}
                selected={selectedTab}
                add={false}
                onAdd={() => { }}
                isFlat={true}
                className="w-full"
            >
                <div>
                    {getContent(selectedTab)}
                </div>
            </Tabs>
        </ActivityFrame>
}

export default TimeAndAttendance;
